import type { Grid } from "@dyson/shared";

/**
 * Light fades into rock: how bright a tile is by how many steps it is from
 * open space, down to near-black (the last, for any deeper): unexplored rock
 * shades off into the dark rather than stopping in a hard black edge.
 */
export const DEPTH_FADE = [1, 0.78, 0.56, 0.38, 0.24, 0.14, 0.08] as const;

/**
 * How light fades into rock at each tile corner (see DEPTH_FADE): every
 * solid tile's steps from open space are found by a search over `r` and a
 * margin round it (the grid's own edge counts as open), and each corner
 * averages the four tiles round it, so the fade is smooth.
 */
export function depthFade(
  grid: Grid,
  r: { x0: number; y0: number; x1: number; y1: number },
  solidAt: (x: number, y: number) => boolean,
): (cx: number, cy: number) => number {
  const deepest = DEPTH_FADE.length - 1;
  const x0 = Math.max(0, r.x0 - deepest - 1);
  const y0 = Math.max(0, r.y0 - deepest - 1);
  const x1 = Math.min(grid.w, r.x1 + deepest + 1);
  const y1 = Math.min(grid.h, r.y1 + deepest + 1);
  const w = x1 - x0;
  const h = y1 - y0;
  const depth = new Uint8Array(w * h).fill(deepest);
  const queue = new Int32Array(w * h);
  let tail = 0;
  // Open tiles first, then solid ones on the grid's edge (open beyond): the search then goes out in order of distance.
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      if (solidAt(x, y)) continue;
      const i = (y - y0) * w + x - x0;
      depth[i] = 0;
      queue[tail++] = i;
    }
  }
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const i = (y - y0) * w + x - x0;
      if (depth[i] === 0 || (x > 0 && y > 0 && x < grid.w - 1 && y < grid.h - 1)) continue;
      depth[i] = 1;
      queue[tail++] = i;
    }
  }
  for (let head = 0; head < tail; head++) {
    const i = queue[head]!;
    const d = depth[i]! + 1;
    if (d >= deepest) continue;
    const x = i % w;
    const y = (i - x) / w;
    for (const j of [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1]) {
      if (j < 0 || depth[j]! <= d) continue;
      depth[j] = d;
      queue[tail++] = j;
    }
  }
  const tile = (x: number, y: number) => (x < x0 || y < y0 || x >= x1 || y >= y1 ? 1 : DEPTH_FADE[depth[(y - y0) * w + x - x0]!]!);
  return (cx, cy) => (tile(cx - 1, cy - 1) + tile(cx, cy - 1) + tile(cx - 1, cy) + tile(cx, cy)) / 4;
}
